import { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalFooter, Button, FormField, Alert, AlertTitle, AlertDescription } from '../../ui';
import { useClients, useAccountsByClient } from '../../../services/queries';

/**
 * Start a pulse on any account, from the index.
 *
 * This is what makes several checks at once workable: previously the only way
 * to start one was the button on an account's own page, so starting a second
 * meant navigating away and losing sight of the first.
 */
export function TakePulseDialog({ open, onOpenChange, onStart, starting, error }) {
	const { data: clients = [] } = useClients();
	const [clientId, setClientId] = useState('');
	const { data: accounts = [] } = useAccountsByClient(clientId);
	const [accountId, setAccountId] = useState('');
	const [depth, setDepth] = useState(10);

	const selectCls = 'w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:bg-muted';

	const submit = async () => {
		if (!accountId) return;
		await onStart({ accountId, requestedPosts: depth });
	};

	return (
		<Modal open={open} onOpenChange={onOpenChange}>
			<ModalContent size="sm">
				<ModalHeader>
					<ModalTitle>Take a pulse</ModalTitle>
					<ModalDescription>Pick an account and we'll read how people are replying to its recent posts.</ModalDescription>
				</ModalHeader>

				<div className="space-y-4">
				{error && (
					<Alert variant="destructive">
						<AlertTitle>Couldn't start</AlertTitle>
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}

				<FormField label="Client">
					<select
						value={clientId}
						onChange={(e) => { setClientId(e.target.value); setAccountId(''); }}
						className={selectCls}
						aria-label="Client"
					>
						<option value="">Choose a client</option>
						{clients.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
					</select>
				</FormField>

				<FormField label="Account">
					<select
						disabled={!clientId}
						value={accountId}
						onChange={(e) => setAccountId(e.target.value)}
						className={selectCls}
						aria-label="Account"
					>
						<option value="">Choose an account</option>
						{accounts.map((a) => <option key={a._id} value={a._id}>@{a.handle} · {a.platform}</option>)}
					</select>
				</FormField>

				<FormField label="How far back">
					<select
						value={depth}
						onChange={(e) => setDepth(Number(e.target.value))}
						className={selectCls}
						aria-label="How many recent posts"
					>
						<option value={10}>Last 10 posts · quick</option>
						<option value={20}>Last 20 posts · deeper</option>
					</select>
				</FormField>

				</div>

				<ModalFooter className="gap-2">
					<Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
					<Button onClick={submit} disabled={!accountId || starting}>
						{starting ? 'Starting…' : 'Take the pulse'}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
